import type { ConnectConnectionAdapter } from '@tanstack/ai-client';
import { useAuthStore } from '@src/core/store/auth-store';
import { fetchRefreshToken } from '@src/core/api/fetch-refresh-token';
import { getCurrentOrgSlug } from '@src/shared/utils/get-org-id';
import { isAguiEvent, type AguiEvent } from '../types/agui.types';
import { assistantScope } from '../hooks/use-assistant-scope';

/**
 * The chat endpoint on the CRM API. The org goes in the query string, not the
 * body: DRF's IsOrganizationMember reads `?organization=` and nothing else.
 * Built per-connect so an org switch mid-session is picked up.
 */
function resolveChatUrl(): string {
  // Safe join, so a missing/extra trailing slash on the base never yields "...v1assistant".
  const url = new URL('assistant/chat/', import.meta.env.VITE_CRM_APP_API_URL);
  const org = getCurrentOrgSlug();
  if (org) url.searchParams.set('organization', org);
  return url.toString();
}

/**
 * The SDK's StreamChunk union, derived from the adapter contract —
 * `@tanstack/ai/client` (its home) is an exports-map subpath this tsconfig's
 * moduleResolution cannot see.
 */
export type StreamChunk =
  ReturnType<ConnectConnectionAdapter['connect']> extends AsyncIterable<
    infer TChunk
  >
    ? TChunk
    : never;

/**
 * Per-run request context. The hook owns a mutable ref returning this; the
 * adapter reads it at connect() time so every run carries the backend's
 * expected body — the SDK's own RunAgentInput payload is NOT what the CRM's
 * /assistant/chat/ endpoint speaks.
 */
export interface AguiSendContext {
  requestId?: string;
  files?: File[];
  /** SDK thread key. Client-generated for a new thread, so it is NOT sent. */
  conversationId: string;
  /** The row the server actually has; null until RUN_STARTED reports one. */
  serverConversationId?: number | null;
  message?: string;
  confirmationToken?: number;
  /** false rejects the pending action instead of approving it. */
  approved?: boolean;
  /**
   * Import approvals only (spec §7.3): the draft's `import.rows[].row`
   * numbers the user unticked. Sent as `excluded_rows` (with an explicit
   * `approved: true`) only when non-empty — an omitted key means "every row
   * the card ticked by default", and a non-import approve never carries it.
   */
  excludedRows?: readonly number[];
  recordContext?: { type: string; id: number | string };
}

/**
 * Map a validated AG-UI wire event to a TanStack AI StreamChunk.
 * The wire format is already AG-UI-standard (type + camelCase inside data:),
 * so this only fills SDK-required fields the backend does not send
 * (threadId on run events, role on TEXT_MESSAGE_START). RUN_STARTED's
 * threadId is the SDK's string key; the server's integer conversationId
 * rides along untyped for the hook.
 */
export function toStreamChunk(
  event: AguiEvent,
  ctx: { threadId: string }
): StreamChunk {
  switch (event.type) {
    case 'RUN_STARTED':
      return {
        type: 'RUN_STARTED',
        threadId: String(event.conversationId),
        runId: event.runId,
        conversationId: event.conversationId,
      } as unknown as StreamChunk;
    case 'RUN_FINISHED':
      return {
        type: 'RUN_FINISHED',
        threadId: ctx.threadId,
        runId: event.runId,
      } as StreamChunk;
    case 'RUN_ERROR':
      return {
        type: 'RUN_ERROR',
        threadId: ctx.threadId,
        runId: event.runId,
        message: event.message,
      } as StreamChunk;
    case 'TOOL_CALL_START':
      return {
        type: 'TOOL_CALL_START',
        toolCallId: event.toolCallId,
        toolCallName: event.toolCallName,
        toolName: event.toolCallName, // deprecated alias some SDK paths read
      } as StreamChunk;
    case 'TOOL_CALL_END':
      // `status` rides along untyped: the processor ignores it (the part goes
      // input-complete) and the hook reads it via onChunk for the chips.
      return {
        type: 'TOOL_CALL_END',
        toolCallId: event.toolCallId,
        status: event.status,
      } as unknown as StreamChunk;
    case 'TEXT_MESSAGE_START':
      return {
        type: 'TEXT_MESSAGE_START',
        messageId: event.messageId,
        role: 'assistant',
      } as StreamChunk;
    case 'TEXT_MESSAGE_CONTENT':
      return {
        type: 'TEXT_MESSAGE_CONTENT',
        messageId: event.messageId,
        delta: event.delta,
      } as StreamChunk;
    case 'TEXT_MESSAGE_END':
      return {
        type: 'TEXT_MESSAGE_END',
        messageId: event.messageId,
      } as StreamChunk;
    case 'CUSTOM':
      return {
        type: 'CUSTOM',
        name: event.name,
        value: event.value,
      } as StreamChunk;
  }
}

/**
 * Protocol failure on the /chat AG-UI stream: a truncated run (clean close
 * with no RUN_FINISHED/RUN_ERROR) or a frame violating the wire contract.
 * The connection generator surfaces it as a terminal RUN_ERROR chunk so the
 * SDK/hook error state fires and the panel shows a recoverable error instead
 * of a frozen spinner or a silently truncated reply.
 */
export class AguiStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AguiStreamError';
  }
}

/**
 * The server refused the request before any stream started (a non-2xx
 * response). `firstMessage` is the first message string in the body, in the
 * server's own words — the DRF field-error shape `{"field": ["..."]}` or
 * `{"detail": "..."}` — or '' when the body carries none.
 */
export class AguiHttpError extends Error {
  readonly status: number;
  readonly firstMessage: string;
  constructor(message: string, status: number, firstMessage: string) {
    super(message);
    this.name = 'AguiHttpError';
    this.status = status;
    this.firstMessage = firstMessage;
  }
}

/** The first message string of a DRF error body, or '' when there is none. */
function firstErrorMessage(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const record = data as Record<string, unknown>;
  if (typeof record.detail === 'string') return record.detail;
  for (const value of Object.values(record)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  }
  return '';
}

/** Parse complete `data:` SSE lines out of a buffer; returns the remainder. */
function splitSseLines(buffer: string): { lines: string[]; rest: string } {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');
  const rest = lines.pop() ?? '';
  return { lines: lines.filter((l) => l.startsWith('data:')), rest };
}

function parseDataLine(line: string): AguiEvent | null {
  const raw = line.slice(5).replace(/^ /, '');
  if (!raw || raw === '[DONE]') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Fail fast: our backend is the only producer, so a frame that isn't JSON
    // means something is genuinely wrong. Never echo the payload itself.
    throw new AguiStreamError('malformed JSON frame in chat stream');
  }
  if (isAguiEvent(parsed)) return parsed;

  // Non-fatal drops (forward compatibility): a CUSTOM frame whose `name` we do
  // not recognize, or a `presentation` whose block fails validation, is skipped
  // — a newer backend may emit CUSTOM events this client version predates, and
  // one bad structured block must not tear down the whole chat stream. Returning
  // null continues the stream with no chunk emitted.
  if (
    typeof parsed === 'object' &&
    parsed !== null &&
    (parsed as Record<string, unknown>)['type'] === 'CUSTOM'
  ) {
    const name = (parsed as Record<string, unknown>)['name'];
    // These are known names whose payloads the guard enforces strictly — a
    // malformed one is a genuine protocol violation, so it falls through to the
    // throw below. Dropping a bad `messages_superseded` in particular would
    // leave an answer on screen that the server has already retracted. Every
    // other name (unknown, or a presentation that didn't validate) is dropped.
    if (name !== 'confirmation_required' && name !== 'messages_superseded') {
      return null;
    }
  }

  // isAguiEvent is strict on tool events (name only, never args) — a frame it
  // rejects here is a protocol violation (possibly a leak-guard rejection), and
  // silently skipping it would hide that. Surface the event type only.
  const t =
    typeof parsed === 'object' &&
    parsed !== null &&
    typeof (parsed as Record<string, unknown>)['type'] === 'string'
      ? ((parsed as Record<string, unknown>)['type'] as string)
      : 'unknown';
  throw new AguiStreamError(
    `contract-violating frame in chat stream (type: ${t})`
  );
}

async function postChat(
  url: string,
  body: Record<string, unknown> | FormData,
  signal: AbortSignal | undefined,
  fetchClient: typeof globalThis.fetch
): Promise<Response> {
  const doFetch = (token: string | null) =>
    fetchClient(url, {
      method: 'POST',
      headers: {
        ...(body instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body instanceof FormData ? body : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });

  let res = await doFetch(useAuthStore.getState().accessToken);
  if (res.status === 401) {
    const fresh = await fetchRefreshToken();
    if (fresh) res = await doFetch(fresh);
  }
  return res;
}

export interface AguiConnectionOptions {
  /** Injected for tests; defaults to global fetch. */
  fetchClient?: typeof globalThis.fetch;
  url?: string;
}

/**
 * ConnectConnectionAdapter for the Ticlick `/chat` endpoint (AG-UI stream).
 *
 * POSTs the backend's expected body ({message?, conversation?,
 * confirmation_token?, record_context?}) with the same bearer +
 * refresh-retry mechanism as the axios assistant client, and yields
 * validated AG-UI events as StreamChunks.
 */
export function createAguiConnection(
  getSendContext: () => AguiSendContext,
  options: AguiConnectionOptions = {}
): ConnectConnectionAdapter {
  return {
    async *connect(_messages, _data, abortSignal): AsyncGenerator<StreamChunk> {
      const startedScope = assistantScope();
      const send = getSendContext();
      const body: Record<string, unknown> = {
        ...(send.requestId ? { request_id: send.requestId } : {}),
        // Omitted on a brand-new thread: the server creates the conversation and
        // reports its real id on RUN_STARTED. Sending our client-side thread key
        // would just be rejected as "Conversation not found."
        ...(send.serverConversationId
          ? { conversation: send.serverConversationId }
          : {}),
        ...(send.message !== undefined ? { message: send.message } : {}),
        ...(send.confirmationToken
          ? { confirmation_token: send.confirmationToken }
          : {}),
        ...(send.approved === false ? { approved: false } : {}),
        // §7.3: excluded_rows only on an import approval with a row unticked,
        // and always alongside an explicit approved: true. Any other request
        // (a normal card's approve included) must not carry the key at all —
        // even [] is refused there.
        ...(send.excludedRows?.length && send.approved !== false
          ? { approved: true, excluded_rows: [...send.excludedRows] }
          : {}),
        ...(send.recordContext ? { record_context: send.recordContext } : {}),
      };

      const fetchClient = options.fetchClient ?? fetch;
      let payload: Record<string, unknown> | FormData = body;
      if (send.files?.length) {
        const form = new FormData();
        Object.entries(body).forEach(([key, value]) => {
          form.append(
            key,
            typeof value === 'string' ? value : JSON.stringify(value)
          );
        });
        send.files.forEach((file) => form.append('files', file));
        payload = form;
      }
      const res = await postChat(
        options.url ?? resolveChatUrl(),
        payload,
        abortSignal,
        fetchClient
      );

      if (!res.ok || !res.body) {
        let detail = `request failed (${res.status})`;
        let firstMessage = '';
        try {
          const data = await res.json();
          firstMessage = firstErrorMessage(data);
          detail =
            typeof data.detail === 'string'
              ? data.detail
              : Object.values(data)
                  .flat()
                  .filter((value) => typeof value === 'string')
                  .join(' ') || detail;
        } catch {
          /* keep default */
        }
        throw new AguiHttpError(detail, res.status, firstMessage);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const ctx = { threadId: send.conversationId };
      let buffer = '';
      // Every run must end with RUN_FINISHED or RUN_ERROR; a clean close
      // without one is a truncated stream (network drop, dead proxy), not
      // success — track it so we can fail instead of returning silently.
      let sawTerminal = false;
      let runId = 'run-unknown';
      const isTerminal = (e: AguiEvent) =>
        e.type === 'RUN_FINISHED' || e.type === 'RUN_ERROR';
      try {
        try {
          while (!abortSignal?.aborted) {
            const { done, value } = await reader.read();
            if (assistantScope() !== startedScope) return;
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const { lines, rest } = splitSseLines(buffer);
            buffer = rest;
            for (const line of lines) {
              const event = parseDataLine(line);
              if (!event) continue;
              if (event.type === 'RUN_STARTED') runId = event.runId;
              if (isTerminal(event)) sawTerminal = true;
              yield toStreamChunk(event, ctx);
            }
          }
          // Flush a final data line not terminated by a newline.
          buffer += decoder.decode();
          if (assistantScope() !== startedScope) return;
          if (buffer.trim().startsWith('data:')) {
            const event = parseDataLine(buffer.trim());
            if (event) {
              if (event.type === 'RUN_STARTED') runId = event.runId;
              if (isTerminal(event)) sawTerminal = true;
              yield toStreamChunk(event, ctx);
            }
          }
          // An intentional abort is the user's cancel, not a protocol failure.
          if (!sawTerminal && !abortSignal?.aborted) {
            throw new AguiStreamError('stream ended before RUN_FINISHED');
          }
        } catch (err) {
          // Surface mid-stream failures as a terminal RUN_ERROR chunk, NOT a
          // bare throw. A generator throw skips the SDK's processing-complete
          // handshake and leaves its synthesized RUN_ERROR in the shared
          // subscription buffer, where it replays into the NEXT run and
          // poisons the retry (verified against @tanstack/ai-client's connect
          // wrapper). A yielded terminal frame settles this run's bookkeeping,
          // fires the hook error state, and keeps a resend fully clean.
          if (abortSignal?.aborted) throw err;
          yield {
            type: 'RUN_ERROR',
            threadId: ctx.threadId,
            runId,
            message: err instanceof Error ? err.message : 'chat stream failed',
          } as StreamChunk;
          return;
        }
      } finally {
        reader.releaseLock();
      }
    },
  };
}
